import Game from '../../Game';
import IResourceDriver from './IResourceDriver';
import EventEmitter from 'eventemitter3';
import ResourceManager, { ResourceEvent } from './ResourceManager';

export interface IResourceDriverManagerOptions {
    concurrency?: number; // The number of resources to load concurrently.
    autoStart?: boolean; // Start the loading process as soon as a resource is added.
    maxAttempts?: number; // Max of attempts if the loading process fail.
}

export interface IResource {
    url: string;
    attempts: number;
}

export default class ResourceDriverManager extends EventEmitter {
    private resourceManager: ResourceManager;
    private queue: Map<string, IResource>;
    private progress: number;
    private autoStart: boolean;
    private maxAttempts: number;
    private driver: IResourceDriver;

    constructor(
        resourceManager: ResourceManager,
        options: IResourceDriverManagerOptions = {}
    ) {
        super();
        this.resourceManager = resourceManager;
        this.queue = new Map<string, IResource>();

        this.progress = 0;
        this.autoStart = options.autoStart === true;
        this.maxAttempts = options.maxAttempts || Infinity;

        this.driver = new Game.config.resourceDriver();
        this.driver.init(this, resourceManager, options.concurrency);
    }

    /**
     * Queue a resource for loading. Start loading resources by calling `load()`.
     * @param resourceID
     * @param path
     */
    public add(resourceID: string, path?: string): void {
        if (this.resourceManager.exists(resourceID)) return;

        path = path || resourceID;

        this.queue.set(resourceID, { url: path, attempts: 1 });
        this.driver.add(resourceID, path);

        if (this.autoStart) this.load();
    }

    /**
     * Load resources in the queue.
     */
    public async load(): Promise<void> {
        if (this.driver.isLoading()) {
            // Already loading - return when all loading is complete.
            return new Promise(resolve => {
                this.once(ResourceEvent.COMPLETE, resolve);
            });
        }

        if (this.driver.getQueue().length === 0) {
            // Nothing to load - return instantly.
            return;
        }

        this.progress = 0;

        try {
            await this.driver.load();
        } catch (e) {
            throw {
                driverError: e,
                assets: this.queue,
            };
        }

        // If resources have been queued in the mean-time start the load process again.
        if (this.driver.getQueue().length > 0) {
            await this.load();
        }

        // If still have queued resources should add to the driver and try to load them again.
        if (this.queue.size > 0) {
            await Game.ticker.setTimeoutPromise(1000);
            this.queue.forEach((resource: IResource, id: string) =>
                this.driver.add(id, resource.url)
            );
            await this.load();
        }

        this.emit(ResourceEvent.COMPLETE);
    }

    /**
     * Stop the loading process. It's stops only the resources that still are in the queue.
     */
    public stop(): void {
        this.driver
            .getQueue()
            .forEach(item => this.queue.delete(item.resourceID));
        this.driver.stop();
    }

    /**
     * To be set by resource driver.
     * @param progress
     */
    public setProgress(progress: number): void {
        this.progress = progress;
        this.emit(ResourceEvent.PROGRESS, this.progress);
    }

    /**
     * Return if the resource is queued.
     * @param resourceID
     */
    public isQueued(resourceID: string): boolean {
        return this.queue.has(resourceID);
    }

    /**
     * Remove the resource from the queue.
     * @param resourceID
     */
    public removeFromQueue(resourceID: string): void {
        this.queue.delete(resourceID);
    }

    /**
     * When a resouce fails in the load process check if it have to try to load again.
     * @param resourceID
     */
    public failToLoad(resourceID: string): void {
        if (!this.isQueued(resourceID)) return;

        const resource = this.queue.get(resourceID);
        if (resource.attempts < this.maxAttempts) {
            // Increase the number of attempts.
            resource.attempts += 1;
        } else {
            this.removeFromQueue(resourceID);
        }
    }
}
